import { Badge, Card, EmptyState, SkeletonText } from '@ashniva/ui';
import { Link, useSearchParams } from 'react-router';

import { formatDateTime } from '../../../shared/lib/format';
import { useAnalysesQuery } from '../api-changes';
import { shortCommit } from '../freshness';
import { ANALYSIS_STATUS_LABELS, RISK } from '../ri-text';
import { useWorkspace } from '../workspace-context';
import { ProblemNotice } from './ProblemNotice';

const SOURCE_LABELS = {
  commit_range: 'Commits',
  task_commits: 'A task’s commits',
  pull_request: 'Pull request',
} as const;

/** The organization's analyses of this repository, newest first. */
export function RecentAnalyses({ current }: { current: string | null }) {
  const { linkId } = useWorkspace();
  const [params] = useSearchParams();
  const list = useAnalysesQuery(linkId, true);
  const hrefFor = (id: string) => {
    const next = new URLSearchParams(params);
    next.set('analysis', id);
    return `?${next.toString()}`;
  };
  return (
    <Card title="Recent analyses">
      {list.isLoading ? (
        <div aria-busy="true">
          <span className="sr-only">Loading analyses</span>
          <SkeletonText lines={3} />
        </div>
      ) : null}
      {list.isError ? (
        <ProblemNotice error={list.error} onRetry={() => void list.refetch()} size="sm" />
      ) : null}
      {list.isSuccess && list.data.items.length === 0 ? (
        <EmptyState size="sm" icon="○" title="No analyses of this repository yet" />
      ) : null}
      {list.isSuccess && list.data.items.length > 0 ? (
        <ul className="ri-list">
          {list.data.items.map((item) => (
            <li key={item.id} className="ri-row">
              <Link
                className="ri-file-link"
                to={hrefFor(item.id)}
                aria-current={item.id === current ? 'true' : undefined}
              >
                {SOURCE_LABELS[item.source]}
                {item.base || item.head
                  ? ` ${shortCommit(item.base) ?? '?'} → ${shortCommit(item.head) ?? '?'}`
                  : ''}
              </Link>
              <span className="ri-muted">
                {ANALYSIS_STATUS_LABELS[item.status]} · {formatDateTime(item.createdAt)}
              </span>
              {item.riskLevel ? (
                <Badge tone={RISK[item.riskLevel].tone}>Risk: {RISK[item.riskLevel].label}</Badge>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}
